import { FC } from 'react';
import { StanModal, StanTimezoneSelector } from '@/components';
import { DateTimePicker } from '@/features/email-flows/components/DateTimePicker/DateTimePicker';
import { useScheduleBroadcastModal } from '../hooks/useScheduleBroadcastModal';

export interface ScheduleData {
    selectedDate: Date;
    selectedTime: number;
    selectedTimezone: string;
}

interface ScheduleBroadcastModalProps {
    isOpen: boolean;
    savedSchedule?: ScheduleData;
    recipientCount?: number;
    disabled?: boolean;
    isLoading?: boolean;
    onClose: () => void;
    onSchedule: (schedule: ScheduleData) => Promise<void>;
    onSendNow: () => void;
}

export const ScheduleBroadcastModal: FC<ScheduleBroadcastModalProps> = ({
    isOpen,
    savedSchedule,
    recipientCount = 0,
    disabled = false,
    isLoading = false,
    onClose,
    onSchedule,
    onSendNow,
}) => {
    const {
        selectedDate,
        selectedTime,
        selectedTimezone,
        errorMessage,
        setSelectedDate,
        setSelectedTime,
        setSelectedTimezone,
        handleSchedule,
    } = useScheduleBroadcastModal({
        isOpen,
        savedSchedule,
        onSchedule,
    });

    const handleSendNow = () => {
        onSendNow();
        onClose();
    };

    // Get minimum date (today)
    const minDate = new Date();
    minDate.setHours(0, 0, 0, 0);

    return (
        <StanModal
            id='schedule-broadcast-modal'
            isOpen={isOpen}
            onClose={onClose}
            title='Schedule Email'
            size='md'
            primaryActionLabel='Schedule'
            onPrimaryAction={handleSchedule}
            primaryActionDisabled={disabled || recipientCount === 0 || isLoading}
            primaryActionLoading={isLoading}
            secondaryActionLabel='Send Now'
            onSecondaryAction={handleSendNow}
            secondaryActionDisabled={disabled || recipientCount === 0 || isLoading}
        >
            <div className='space-y-4'>
                {recipientCount === 0 && (
                    <div className='border-warning-intense bg-warning-dimmed mb-4 rounded-md border px-4 py-3'>
                        <p className='text-warning-bold text-caption-1-regular text-center'>
                            Please select at least one audience tag before scheduling this broadcast.
                        </p>
                    </div>
                )}

                <p className='text-label-sm-bold mb-4'>When should we send it out?</p>

                <div>
                    <label htmlFor='broadcast-timezone' className='text-label-xs-bold'>
                        Timezone
                    </label>
                    <StanTimezoneSelector
                        id='broadcast-timezone'
                        name='broadcast-timezone'
                        selectedTimezone={selectedTimezone}
                        onTimezoneChange={setSelectedTimezone}
                    />
                </div>

                <DateTimePicker
                    selectedDate={selectedDate}
                    selectedTime={selectedTime}
                    onDateChange={date => date && setSelectedDate(date)}
                    onTimeChange={setSelectedTime}
                    minDate={minDate}
                    error={!!errorMessage}
                    errorMessage={errorMessage}
                />
            </div>
        </StanModal>
    );
};
